<template>
  <v-col
    cols="12"
    class="offer-monitoring-page"
  >
    <v-card>
      <v-card-title>
        Offres spéciales : vue d'ensemble
        <v-spacer></v-spacer>
        <v-btn
          small
          color="info"
          :loading="loading"
          @click="refreshAll"
        >
          <v-icon
            small
            class="mr-1"
          >
            {{ icons.mdiRefresh }}
          </v-icon>
          Actualiser
        </v-btn>
      </v-card-title>
      <v-card-text>
        <p class="offer-meta">
          Deux sources distinctes : les définitions d'offres GetSpecialOffers ci-dessous et les séjours tarifés
          GetLastMinuteOffers dans le bloc dédié en bas de page. Les compteurs ne sont pas additionnés.
          Tous campings, y compris désactivés. Les offres personnalisées du BO ne sont pas incluses.
        </p>
        <h2>GetSpecialOffers : définitions d'offres</h2>
        <v-alert
          v-if="refreshError"
          type="error"
          outlined
          role="alert"
        >
          {{ refreshError }}
        </v-alert>
        <v-progress-linear
          v-if="loading && !inventory"
          indeterminate
          color="info"
          aria-label="Chargement des statistiques des offres"
        ></v-progress-linear>
        <section
          class="offer-metrics"
          aria-label="Statistiques des offres"
        >
          <div
            v-for="metric in metrics"
            :key="metric.label"
            class="offer-metric"
          >
            <div class="text-caption text-uppercase">
              {{ metric.label }}
            </div>
            <div class="text-h6 font-weight-bold offer-number">
              {{ formatNumber(metric.value) }}
            </div>
            <div class="offer-meta">
              {{ metric.hint }}
            </div>
          </div>
        </section>

        <template v-if="inventory">
          <section class="offer-band">
            <h2>Répartition par famille</h2>
            <v-data-table
              :headers="familyHeaders"
              :items="inventory.byType"
              item-key="type"
              hide-default-footer
              disable-sort
              class="offer-table"
            >
              <template v-slot:item.type="{ item }">
                <v-btn
                  text
                  small
                  color="info"
                  @click="showFamily(item.type)"
                >
                  {{ typeLabel(item.type) }}
                </v-btn>
              </template>
              <template v-slot:item.offerCount="{ item }">
                <strong class="offer-number">{{ formatNumber(item.offerCount) }}</strong>
              </template>
            </v-data-table>
            <p class="offer-meta mt-4 mb-0">
              État des périodes de séjour au {{ formatDay(inventory.referenceDate) }} : en cours si une période couvre aujourd'hui,
              sinon à venir si une période future existe, sinon terminées. Les dates invalides ou absentes sont à vérifier.
              Ces compteurs ne garantissent ni disponibilité, ni réservation possible, ni visibilité sur le site.
            </p>
          </section>

          <div class="offer-splits">
            <section class="offer-band">
              <div class="offer-heading">
                <h2>Catégories et super-catégories</h2>
                <router-link :to="{ name: 'specialOfferList' }">
                  Gérer les regroupements
                </router-link>
              </div>
              <dl class="offer-definition-list">
                <template v-for="row in categoryMetrics">
                  <dt :key="row.label + '-label'">
                    {{ row.label }}
                  </dt>
                  <dd :key="row.label + '-value'">
                    {{ formatNumber(row.value) }}
                  </dd>
                </template>
              </dl>
              <p class="offer-meta mb-0">
                Une catégorie vide configurée est conservée pour retrouver son rattachement lors d'un prochain import.
                L'absence de super-catégorie n'est pas une erreur d'import.
                Une offre sans AutomaticLabelId reçoit sa propre catégorie lors du nouvel import.
              </p>
            </section>
            <section class="offer-band">
              <h2>Qualité et langues</h2>
              <dl class="offer-definition-list">
                <dt>Type absent ou non reconnu en BDD (à réimporter)</dt>
                <dd>{{ formatNumber(unrecognizedCount) }}</dd>
                <dt>Sans hébergement associé ni ciblage général</dt>
                <dd>{{ formatNumber(inventory.missingTargetCount) }}</dd>
                <dt>Sans catégorie</dt>
                <dd>{{ formatNumber(inventory.missingCategoryCount) }}</dd>
                <dt>Sans super-catégorie (offres catégorisées)</dt>
                <dd>{{ formatNumber(inventory.withoutSuperCategoryCount) }}</dd>
                <dt>Offres avec identité en doublon</dt>
                <dd>{{ formatNumber(inventory.duplicateCount) }}</dd>
              </dl>
              <div
                v-for="translation in inventory.translations"
                :key="translation.locale"
                class="offer-language"
              >
                <span>{{ translation.locale.toUpperCase() }}</span>
                <v-progress-linear
                  :value="percentage(translation.offerCount, inventory.offerCount)"
                  color="info"
                  height="5"
                  :aria-label="translation.locale + ' : ' + translation.offerCount + ' offres avec libellé'"
                ></v-progress-linear>
                <span class="offer-number">{{ formatNumber(translation.offerCount) }} / {{ formatNumber(inventory.offerCount) }}</span>
              </div>
              <p class="offer-meta mb-0">
                Ces points de contrôle ne signifient pas un échec d'import : une offre peut être importée sans ciblage local exploitable.
                Les langues comptent les libellés non vides. Les associations fournisseur ignorées lors des imports ne sont pas historisées :
                une association partiellement manquante ne peut pas être mesurée ici.
              </p>
            </section>
          </div>
          <p class="offer-meta mb-6">
            Synthèse calculée le {{ formatDate(inventory.computedAt) }} (cache de 60 s, heure de Paris).
            Il ne s'agit pas de la date du dernier import : son historique n'est pas enregistré pour ces offres.
            Les filtres ci-dessous n'affectent pas la synthèse globale.
          </p>
        </template>

        <section
          ref="inventorySection"
          class="offer-band"
        >
          <v-tabs
            v-model="tab"
            color="info"
            show-arrows
            class="mb-5"
          >
            <v-tab>Couverture par camping</v-tab>
            <v-tab>Inventaire des offres</v-tab>
          </v-tabs>
          <div v-show="tab === 0">
            <form
              class="offer-camping-filters"
              @submit.prevent="filtersChanged('campings')"
            >
              <v-text-field
                v-model="campingSearch"
                label="Nom du camping ou identifiant SH"
                outlined
                dense
                hide-details
                maxlength="120"
                clearable
              ></v-text-field>
              <v-select
                v-model="coverage"
                :items="coverageOptions"
                label="Couverture"
                outlined
                dense
                hide-details
                @change="filtersChanged('campings')"
              ></v-select>
              <v-btn
                type="submit"
                color="info"
                outlined
                :disabled="loading"
              >
                Rechercher
              </v-btn>
            </form>
            <v-data-table
              :headers="campingHeaders"
              :items="campings"
              :loading="loading"
              :items-per-page="25"
              hide-default-footer
              disable-sort
              class="offer-table"
              no-data-text="Aucun camping ne correspond à ces filtres."
              loading-text="Chargement des campings..."
            >
              <template v-slot:item.name="{ item }">
                <span class="offer-camping-name">{{ item.name || 'Camping sans nom' }}</span>
                <div class="offer-meta">
                  SH {{ item.establishmentId || '-' }} · {{ item.enabled ? 'Activé' : 'Désactivé' }}
                </div>
              </template>
              <template v-slot:item.issueCount="{ item }">
                <span :class="item.issueCount ? 'warning--text font-weight-bold' : ''">{{ formatNumber(item.issueCount) }}</span>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn
                  small
                  text
                  color="info"
                  :aria-label="'Voir les offres de ' + (item.name || item.establishmentId)"
                  @click="showCamping(item)"
                >
                  Voir les offres
                </v-btn>
              </template>
            </v-data-table>
            <div class="offer-footer">
              <span class="offer-meta">{{ formatNumber(campingTotal) }} campings</span>
              <v-pagination
                v-if="campingTotal > 25"
                v-model="campingPage"
                :length="Math.ceil(campingTotal / 25)"
                :total-visible="5"
                color="info"
                @input="refresh"
              ></v-pagination>
            </div>
          </div>

          <div v-show="tab === 1">
            <v-chip
              v-if="selectedCamping"
              close
              outlined
              color="info"
              class="mb-4"
              @click:close="clearCamping"
            >
              {{ selectedCamping.name || 'Camping' }} · SH {{ selectedCamping.establishmentId }}
            </v-chip>
            <form @submit.prevent="filtersChanged('offers')">
              <div class="offer-filters">
                <v-text-field
                  v-model="offerSearch"
                  label="Offre, catégorie, camping ou identifiant SH"
                  outlined
                  dense
                  hide-details
                  maxlength="120"
                  clearable
                ></v-text-field>
                <v-select
                  v-model="type"
                  :items="typeOptions"
                  label="Famille"
                  outlined
                  dense
                  hide-details
                  @change="filtersChanged('offers')"
                ></v-select>
                <v-select
                  v-model="status"
                  :items="statusOptions"
                  label="Périodes de séjour"
                  outlined
                  dense
                  hide-details
                  @change="filtersChanged('offers')"
                ></v-select>
                <v-select
                  v-model="issue"
                  :items="issueOptions"
                  label="Points de contrôle"
                  outlined
                  dense
                  hide-details
                  @change="filtersChanged('offers')"
                ></v-select>
              </div>
              <div class="offer-controls mb-5">
                <v-btn
                  type="submit"
                  color="info"
                  outlined
                  :disabled="loading"
                >
                  Rechercher
                </v-btn>
                <v-btn
                  text
                  :disabled="loading"
                  @click="resetOffers"
                >
                  Réinitialiser les filtres
                </v-btn>
              </div>
            </form>
            <v-data-table
              :headers="offerHeaders"
              :items="offers"
              :loading="loading"
              :items-per-page="25"
              hide-default-footer
              disable-sort
              show-expand
              class="offer-table"
              no-data-text="Aucune offre ne correspond à ces filtres."
              loading-text="Chargement des offres..."
            >
              <template v-slot:item.name="{ item }">
                <strong class="offer-name">{{ item.name || 'Libellé FR absent' }}</strong>
                <div class="offer-meta">
                  {{ typeLabel(item.type) }} · SH {{ item.sourceId || '-' }}
                </div>
              </template>
              <template v-slot:item.campingName="{ item }">
                <span class="offer-camping-name">{{ item.campingName || 'Camping non renseigné' }}</span>
                <div class="offer-meta">
                  SH {{ item.establishmentId || '-' }}
                </div>
              </template>
              <template v-slot:item.categoryName="{ item }">
                <div>{{ item.categoryName || (item.categoryId ? 'Libellé FR absent' : 'Sans catégorie') }}</div>
                <div class="offer-meta">
                  Clé : {{ item.categoryKey || '-' }}
                </div>
                <router-link
                  v-if="item.superCategoryId"
                  :to="{ name: 'specialOfferDetails', params: { specialOfferId: item.superCategoryId } }"
                >
                  {{ item.superCategoryName || 'Super-catégorie' }}
                </router-link>
                <div
                  v-else
                  class="offer-meta"
                >
                  Sans super-catégorie
                </div>
              </template>
              <template v-slot:item.status="{ item }">
                <v-chip
                  small
                  label
                  outlined
                  :color="statusColor(item.status)"
                >
                  {{ statusLabel(item.status) }}
                </v-chip>
              </template>
              <template v-slot:item.issues="{ item }">
                <div
                  v-for="code in item.issues"
                  :key="code"
                  class="warning--text offer-issue"
                >
                  {{ issueLabel(code) }}
                </div>
                <span v-if="!item.issues.length">Aucun signal</span>
              </template>
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length">
                  <div class="offer-expanded">
                    <div class="offer-detail-summary">
                      <div>
                        <h3>Ciblage enregistré</h3>
                        <div>{{ formatNumber(item.accommodationCount) }} hébergement(s) associé(s)</div>
                        <div v-if="item.onAllAccommodations">
                          Tous les locatifs
                        </div>
                        <div v-if="item.onAllPitches">
                          Tous les emplacements
                        </div>
                        <div v-if="!item.onAllAccommodations && !item.onAllPitches && !item.accommodationCount">
                          Aucun ciblage local exploitable
                        </div>
                      </div>
                      <div>
                        <h3>Traductions et identité</h3>
                        <div>{{ item.locales.length ? item.locales.join(', ').toUpperCase() : 'Aucun libellé renseigné' }}</div>
                        <div class="offer-id mt-2">
                          {{ item.id }}
                        </div>
                      </div>
                    </div>
                    <h3>Périodes enregistrées</h3>
                    <div class="offer-periods">
                      <div
                        v-for="(period, index) in item.periods"
                        :key="index"
                        class="offer-period"
                      >
                        <strong>{{ formatDay(period.start) }} au {{ formatDay(period.end) }}</strong>
                        <div class="offer-meta">
                          Réservation : {{ formatDay(period.bookingStart) }} au {{ formatDay(period.bookingEnd) }}
                        </div>
                        <div
                          v-if="period.deadline"
                          class="offer-meta"
                        >
                          {{ deadlineLabel(item.type) }} : {{ formatDay(period.deadline) }}
                        </div>
                      </div>
                      <span v-if="!item.periods.length">Aucune période</span>
                    </div>
                  </div>
                </td>
              </template>
            </v-data-table>
            <div class="offer-footer">
              <span class="offer-meta">{{ formatNumber(offerTotal) }} offres · Une ligne par camping, famille et offre fournisseur (hors doublons historiques).</span>
              <v-pagination
                v-if="offerTotal > 25"
                v-model="offerPage"
                :length="Math.ceil(offerTotal / 25)"
                :total-visible="5"
                color="info"
                @input="refresh"
              ></v-pagination>
            </div>
          </div>
          <div class="offer-meta mt-4">
            Listes actualisées le {{ formatDate(lastUpdated) }}
          </div>
        </section>
        <last-minute-monitoring ref="lastMinute"></last-minute-monitoring>
      </v-card-text>
    </v-card>
  </v-col>
</template>

<script>
import axios from 'axios'
import { mdiRefresh } from '@mdi/js'
import config from '@/config'
import { formatNumber, formatDate, formatDay, percentage } from '@/views/Stay/monitoring'
import { typeLabel, statusLabel, statusColor, issueLabel, deadlineLabel, typeOptions, statusOptions, issueOptions, coverageOptions } from './labels'
import LastMinuteMonitoring from './LastMinute.vue'

export default {
  components: { LastMinuteMonitoring },
  data: () => ({
    inventory: null, campings: [], offers: [], campingTotal: 0, offerTotal: 0,
    loading: false, refreshError: null, lastUpdated: null, tab: 0,
    campingPage: 1, campingSearch: '', coverage: 'all', offerPage: 1, offerSearch: '',
    type: 'all', status: 'all', issue: 'all', selectedCamping: null,
    icons: { mdiRefresh }, typeOptions, statusOptions, issueOptions, coverageOptions,
    familyHeaders: [{ text: 'Famille', value: 'type' }, { text: 'Offres en base', value: 'offerCount', align: 'end' },
      { text: 'En cours', value: 'currentCount', align: 'end' }, { text: 'À venir', value: 'upcomingCount', align: 'end' },
      { text: 'Terminées', value: 'pastCount', align: 'end' }, { text: 'Dates à vérifier', value: 'undatedCount', align: 'end' },
      { text: 'Campings', value: 'campingCount', align: 'end' }],
    campingHeaders: [{ text: 'Camping', value: 'name' }, { text: 'Classiques', value: 'classicCount', align: 'end' },
      { text: 'DMDS (définitions)', value: 'dmdsCount', align: 'end' }, { text: 'Sur-offres', value: 'overDiscountCount', align: 'end' },
      { text: 'Total', value: 'offerCount', align: 'end' }, { text: 'En cours', value: 'currentCount', align: 'end' },
      { text: 'À vérifier', value: 'issueCount', align: 'end' }, { text: '', value: 'actions' }],
    offerHeaders: [{ text: 'Offre', value: 'name' }, { text: 'Camping', value: 'campingName' },
      { text: 'Regroupement', value: 'categoryName' }, { text: 'Périodes', value: 'status' }, { text: 'Points de contrôle', value: 'issues' }]
  }),
  computed: {
    unrecognizedCount () {
      const family = this.inventory && this.inventory.byType.find(row => row.type === 'unknown')

      return family ? family.offerCount : 0
    },
    metrics () {
      const s = this.inventory || {}

      return [{ label: 'Définitions en base', value: s.offerCount, hint: 'GetSpecialOffers : classiques, DMDS, sur-offres et historique' },
        { label: 'Campings avec offres', value: s.campingCount, hint: this.inventory ? `${formatNumber(s.campingsWithoutOffers)} sans offre sur ${formatNumber(s.configuredCampingCount)} suivis` : '' },
        { label: 'Périodes en cours', value: s.currentCount, hint: 'Offres couvrant la date du jour' },
        { label: 'Offres avec un point de contrôle', value: s.issueCount, hint: 'Données historiques ou ciblage incomplet, une seule fois par offre' }]
    },
    categoryMetrics () {
      const c = this.inventory.categories

      return [{ label: 'Catégories en base', value: c.total },
        { label: 'Catégories avec au moins une offre', value: this.inventory.usedCategoryCount },
        { label: 'Rattachées à une super-catégorie', value: c.linkedToSuperCategory },
        { label: 'Vides, conservées car configurées', value: c.retainedEmpty },
        { label: 'Vides, éligibles au nettoyage', value: c.removable },
        { label: 'Groupes de clés en doublon', value: c.duplicateGroups },
        { label: 'Catégories redondantes', value: c.redundant },
        { label: 'Super-catégories en base', value: c.superCategoryCount },
        { label: 'Super-catégories activées', value: c.enabledSuperCategoryCount }]
    }
  },
  created () {
    this.requestSequence = 0
    this.requestCancellation = null
    this.refresh()
  },
  beforeDestroy () {
    ++this.requestSequence
    if (this.requestCancellation) this.requestCancellation.cancel()
  },
  methods: {
    formatNumber, formatDate, formatDay, percentage, typeLabel, statusLabel, statusColor, issueLabel, deadlineLabel,
    refreshAll () {
      this.refresh()
      if (this.$refs.lastMinute) this.$refs.lastMinute.refresh()
    },
    getMonitoring (path, cancelToken, params = {}) {
      return this.$http.get(config.apiUrl + '/api/special-offer/' + path, {
        params, cancelToken, headers: { Authorization: 'Bearer ' + this.$store.getters.getToken }
      }).then(response => response.data)
    },
    async refresh () {
      if (this.requestCancellation) this.requestCancellation.cancel()
      this.requestCancellation = axios.CancelToken.source()
      const sequence = ++this.requestSequence
      const token = this.requestCancellation.token
      const campingFilters = { page: this.campingPage, limit: 25, search: this.campingSearch || '', coverage: this.coverage }
      const offerFilters = { page: this.offerPage, limit: 25, search: this.offerSearch || '', type: this.type,
        status: this.status, issue: this.issue, campingId: this.selectedCamping ? this.selectedCamping.id : undefined }
      this.loading = true
      this.refreshError = null
      try {
        const summary = await this.getMonitoring('summary', token)
        const campings = await this.getMonitoring('campings', token, campingFilters)
        const offers = await this.getMonitoring('offers', token, offerFilters)
        if (sequence !== this.requestSequence) return
        Object.assign(this, { inventory: summary.inventory, campings: campings.items, campingTotal: campings.total,
          offers: offers.items, offerTotal: offers.total, lastUpdated: new Date().toISOString() })
      } catch (error) {
        if (axios.isCancel(error) || sequence !== this.requestSequence) return
        const response = error.response
        this.refreshError = response && response.status === 401 ? 'Votre session a expiré. Veuillez vous reconnecter.'
          : 'Impossible de récupérer les statistiques. Les dernières données affichées sont conservées et peuvent ne plus correspondre aux filtres sélectionnés.'
      } finally {
        if (sequence === this.requestSequence) this.loading = false
      }
    },
    filtersChanged (list) {
      if (list === 'campings') this.campingPage = 1
      else this.offerPage = 1
      this.refresh()
    },
    showFamily (type) {
      this.resetOfferFilters()
      this.type = type
      this.openOffers()
    },
    showCamping (camping) {
      this.resetOfferFilters()
      this.selectedCamping = camping
      this.openOffers()
    },
    openOffers () {
      this.tab = 1
      this.filtersChanged('offers')
      this.$nextTick(() => this.$refs.inventorySection.scrollIntoView({ block: 'start' }))
    },
    clearCamping () {
      this.selectedCamping = null
      this.filtersChanged('offers')
    },
    resetOfferFilters () {
      this.type = 'all'
      this.status = 'all'
      this.issue = 'all'
      this.offerSearch = ''
      this.selectedCamping = null
    },
    resetOffers () {
      this.resetOfferFilters()
      this.filtersChanged('offers')
    }
  }
}
</script>

<style lang="scss" src="./monitoring.scss"></style>
